export const FLEET_EMBED_STORAGE_KEY = 'isFleetEmbed';

export const FLEET_EMBED_ORIGINS = [
  'https://fleet.azuga.com',
  'http://localhost:5500',
] as const;

export function isAllowedFleetEmbedOrigin(origin: string): boolean {
  return (FLEET_EMBED_ORIGINS as readonly string[]).includes(origin);
}

export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top can throw in some browsers.
    return true;
  }
}

export function setFleetEmbedInStorage(enabled: boolean): void {
  try {
    if (enabled) {
      window.sessionStorage.setItem(FLEET_EMBED_STORAGE_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(FLEET_EMBED_STORAGE_KEY);
    }
  } catch {
    // sessionStorage may be unavailable in some environments.
  }
}

export function getFleetEmbedFromStorage(): boolean {
  try {
    return window.sessionStorage.getItem(FLEET_EMBED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function tryGetOrigin(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Sync detect: in an iframe whose parent is an allowlisted Fleet/test origin
 * (via referrer or ancestorOrigins), `?isFleet=true` query (Fleet ELOGS),
 * or a previously persisted embed flag.
 */
export function isLikelyFleetEmbed(): boolean {
  if (!isInIframe()) {
    return false;
  }

  if (getFleetEmbedFromStorage()) {
    return true;
  }

  try {
    if (new URL(window.location.href).searchParams.get('isFleet') === 'true') {
      setFleetEmbedInStorage(true);
      return true;
    }
  } catch {
    // Invalid location href; fall through to referrer/ancestor checks.
  }

  const referrerOrigin = document.referrer
    ? tryGetOrigin(document.referrer)
    : null;
  if (referrerOrigin && isAllowedFleetEmbedOrigin(referrerOrigin)) {
    setFleetEmbedInStorage(true);
    return true;
  }

  const ancestors = window.location.ancestorOrigins;
  if (ancestors?.length) {
    for (let i = 0; i < ancestors.length; i += 1) {
      if (isAllowedFleetEmbedOrigin(ancestors[i])) {
        setFleetEmbedInStorage(true);
        return true;
      }
    }
  }

  return false;
}

/** Whether fleet embed mode is currently active (iframe + persisted/detected). */
export function isFleetEmbedActive(): boolean {
  return isInIframe() && (getFleetEmbedFromStorage() || isLikelyFleetEmbed());
}
